import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Users, TrendingUp, DollarSign, ChevronDown, ChevronRight, Calendar, ArrowUpRight, UserPlus } from 'lucide-react';
import { bousanmSolideApi } from '../services/bousanmSolideApi';
import Swal from 'sweetalert2';

const ClientsBS = () => {
  const { t } = useTranslation(['solide', 'common']);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedClient, setExpandedClient] = useState(null);
  
  // État pour l'assignation d'agent
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedClientForAssign, setSelectedClientForAssign] = useState(null);
  const [newAgentUsername, setNewAgentUsername] = useState('');
  const [assignReason, setAssignReason] = useState('Réassignation administrative');
  const [isAssigning, setIsAssigning] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const fetchClients = () => {
    setLoading(true);
    bousanmSolideApi.getAllClientsSolide()
      .then(data => setClients(data.clients || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Charger la liste des agents à l'ouverture du modal
  useEffect(() => {
    if (assignModalOpen && agentsList.length === 0) {
        setLoadingAgents(true);
        bousanmSolideApi.getAgentsList()
            .then(data => setAgentsList(data || []))
            .catch(console.error)
            .finally(() => setLoadingAgents(false));
    }
  }, [assignModalOpen]);

  const handleAssignAgent = async (e) => {
    e.preventDefault();
    if (!selectedClientForAssign || !newAgentUsername) return;

    setIsAssigning(true);
    try {
      await bousanmSolideApi.assignAgent({
        compte_code: selectedClientForAssign.compte.code,
        agent_username: newAgentUsername,
        raison: assignReason
      });
      
      Swal.fire({
        icon: 'success',
        title: t('solide:clients.assignModal.successTitle'),
        text: t('solide:clients.assignModal.successMessage', { username: newAgentUsername }),
        timer: 2000,
        showConfirmButton: false
      });
      setAssignModalOpen(false);
      setNewAgentUsername('');
      // Rafraichir la liste
      fetchClients();
    } catch (error) {
      console.error("Erreur assignation:", error);
      Swal.fire({
        icon: 'error',
        title: t('solide:clients.assignModal.errorTitle'),
        text: t('solide:clients.assignModal.errorMessage')
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const openAssignModal = (client) => {
    setSelectedClientForAssign(client);
    setNewAgentUsername('');
    setAssignModalOpen(true);
  };

  const globalStats = useMemo(() => {
    const total = clients.reduce((acc, c) => {
      const devise = c.financier?.devise || 'HTG';
      const solde = c.financier?.solde_solidere || 0;
      const verse = c.statistiques_paiements?.montant_total_verse || 0;
      
      if (devise === 'USD') {
        acc.epargneTotaleUSD += solde;
        acc.montantVerseTotalUSD += verse;
      } else {
        acc.epargneTotaleHTG += solde;
        acc.montantVerseTotalHTG += verse;
      }
      
      acc.clientsAJour += (c.situation?.est_a_jour ? 1 : 0);
      return acc;
    }, { epargneTotaleHTG: 0, epargneTotaleUSD: 0, montantVerseTotalHTG: 0, montantVerseTotalUSD: 0, clientsAJour: 0 });
    
    return { 
      ...total, 
      tauxReussite: clients.length > 0 ? (total.clientsAJour / clients.length * 100) : 0 
    };
  }, [clients]);

  if (loading && clients.length === 0) return <div className="flex h-full items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  const filtered = clients.filter(c => {
    const searchTerm = search.toLowerCase();
    const nom = c.client?.nom_complet?.toLowerCase() || '';
    const code = c.compte?.code?.toLowerCase() || '';
    const email = c.client?.email?.toLowerCase() || '';
    const tel = c.client?.telephone?.toLowerCase() || '';
    const agent = c.compte?.agent_assigne?.toLowerCase() || '';
    
    return nom.includes(searchTerm) || 
           code.includes(searchTerm) || 
           email.includes(searchTerm) || 
           tel.includes(searchTerm) ||
           agent.includes(searchTerm);
  });

  const toggleExpandClient = (index) => {
    setExpandedClient(expandedClient === index ? null : index);
  };

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-4">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{t('solide:clients.title')}</h1>
          <p className="text-xs text-gray-500">{t('solide:clients.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72 group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input 
              type="text" 
              placeholder={t('solide:clients.searchPlaceholder')} 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="block w-full pl-10 pr-10 py-2 h-10 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-gray-400"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                <div className="bg-gray-200 hover:bg-gray-300 rounded-full p-0.5 transition-colors">
                  <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:clients.stats.totalClients')}</p><div className="text-xl font-bold mt-0.5">{clients.length}</div></div>
          <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center"><Users className="h-4 w-4 text-blue-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:clients.stats.totalSavings')}</p>
            <div className="flex flex-col mt-0.5">
              <div className="text-sm font-bold">{globalStats.epargneTotaleHTG.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">HTG</span></div>
              <div className="text-sm font-bold text-emerald-600">{globalStats.epargneTotaleUSD.toLocaleString()} <span className="text-[10px] font-normal text-emerald-400">$</span></div>
            </div>
          </div>
          <div className="h-8 w-8 rounded-full bg-purple-50 flex items-center justify-center"><DollarSign className="h-4 w-4 text-purple-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:clients.stats.totalPaid')}</p>
            <div className="flex flex-col mt-0.5">
              <div className="text-sm font-bold">{globalStats.montantVerseTotalHTG.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">HTG</span></div>
              <div className="text-sm font-bold text-emerald-600">{globalStats.montantVerseTotalUSD.toLocaleString()} <span className="text-[10px] font-normal text-emerald-400">$</span></div>
            </div>
          </div>
          <div className="h-8 w-8 rounded-full bg-green-50 flex items-center justify-center"><DollarSign className="h-4 w-4 text-green-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:clients.stats.upToDateRate')}</p><div className="text-xl font-bold mt-0.5">{globalStats.tauxReussite.toFixed(1)}%</div></div>
          <div className="h-8 w-8 rounded-full bg-orange-50 flex items-center justify-center"><TrendingUp className="h-4 w-4 text-orange-600" /></div>
        </CardContent></Card>
      </div>

      {/* Modern List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 gap-4 p-3 bg-gray-50/50 border-b text-xs font-medium text-gray-500">
              <div className="col-span-4 pl-2">{t('solide:clients.table.client')}</div>
              <div className="col-span-2">{t('solide:clients.table.assignedAgent')}</div>
              <div className="col-span-2 text-right">{t('solide:clients.table.savings')}</div>
              <div className="col-span-2 text-center">{t('solide:clients.table.progression')}</div>
              <div className="col-span-1 text-center">{t('solide:clients.table.status')}</div>
              <div className="col-span-1"></div>
            </div>
            
            <div className="divide-y">
              {filtered.map((item, i) => {
                const client = item.client || {};
                const compte = item.compte || {};
                const financier = item.financier || {};
                const progression = item.progression || {};
                const paiements = item.statistiques_paiements || {};
                const transactions = item.transactions_recentes?.liste || [];
                const isExpanded = expandedClient === i;
                
                return (
                  <React.Fragment key={i}>
                    <div 
                      className={`grid grid-cols-12 gap-4 p-3 items-center hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                      onClick={() => toggleExpandClient(i)}
                    >
                      <div className="col-span-4 flex items-center gap-3 pl-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">
                          {client.nom_complet?.charAt(0) || 'C'}
                        </div>
                        <div className="overflow-hidden flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{client.nom_complet}</p>
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 font-mono">
                              {compte.code}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-500 truncate">{client.telephone}</p>
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600">
                            {compte.agent_assigne?.charAt(0) || 'A'}
                          </div>
                          <span className="text-xs text-gray-700 truncate">{compte.agent_assigne}</span>
                        </div>
                      </div>

                      <div className="col-span-2 text-right font-medium text-sm text-gray-900">
                        {(financier.solde_solidere || 0).toLocaleString()} <span className="text-[10px] text-gray-400 font-normal">{financier.devise === 'USD' ? '$' : 'G'}</span>
                      </div>

                      <div className="col-span-2 text-center">
                        <div className="w-full bg-gray-200 rounded-full h-1.5 max-w-[100px] mx-auto">
                          <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${progression.pourcentage_completion || 0}%` }}></div>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-0.5 block">
                          {progression.semaines_payees}/{progression.total_semaines} sem. ({progression.pourcentage_completion}%)
                        </span>
                      </div>

                      <div className="col-span-1 text-center">
                        <Badge className={`h-5 text-[10px] ${item.situation?.est_a_jour ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
                          {item.situation?.est_a_jour ? t('solide:clients.table.upToDate') : t('solide:clients.table.late')}
                        </Badge>
                      </div>

                      <div className="col-span-1 flex justify-end pr-2 text-gray-400">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </div>
                    </div>

                    {/* Expanded Details - Dehors de min-w pour s'adapter à la largeur écran si possible, ou rester dedans */}
                    {isExpanded && (
                      <div className="bg-gray-50 p-4 border-t border-b">
                        {/* Le contenu détaillé peut rester adaptatif (flex/grid responsive) */}
                        <div className="bg-white rounded-md border shadow-sm p-4">
                          <div className="flex flex-col lg:flex-row justify-between items-start gap-6 mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 flex-1 w-full">
                              {/* Info Plan & Stats */}
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-[10px] text-blue-600 font-semibold uppercase mb-1">{t('solide:clients.details.activePlan')}</p>
                                <p className="text-sm font-bold text-gray-900">{compte.plan?.toUpperCase() || 'N/A'}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{t('solide:clients.details.start')}: {new Date(compte.date_debut || compte.date_creation).toLocaleDateString()}</p>
                              </div>
                              <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                                <p className="text-[10px] text-green-600 font-semibold uppercase mb-1">{t('solide:clients.details.weeklyAmount')}</p>
                                <p className="text-sm font-bold text-gray-900">{(financier.montant_hebdo || 0).toLocaleString()} {financier.devise === 'USD' ? '$' : 'G'}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{t('solide:clients.details.totalPaid')}: {(financier.montant_verse || 0).toLocaleString()} {financier.devise === 'USD' ? '$' : 'G'}</p>
                              </div>
                              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                <p className="text-[10px] text-purple-600 font-semibold uppercase mb-1">{t('solide:clients.details.accumulatedBonus')}</p>
                                <p className="text-sm font-bold text-gray-900">{(financier.bonis || 0).toLocaleString()} {financier.devise === 'USD' ? '$' : 'G'}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{t('solide:clients.details.punctuality')}: {progression.pourcentage_ponctualite}%</p>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="text-[10px] text-gray-500 font-semibold uppercase mb-1">{t('common:last_payment') || 'Dernier Paiement'}</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {paiements.derniere_transaction_date ? new Date(paiements.derniere_transaction_date).toLocaleDateString() : '-'}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1">{paiements.nombre_transactions} transactions</p>
                              </div>
                            </div>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="shrink-0 border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 gap-2 w-full lg:w-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAssignModal(item);
                              }}
                            >
                              <UserPlus className="h-4 w-4" />
                              {t('solide:clients.details.assignAgent')}
                            </Button>
                          </div>

                          {/* Transactions Récents */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <ArrowUpRight className="h-4 w-4 text-gray-500" />
                              <h3 className="text-xs font-bold text-gray-700 uppercase">{t('solide:clients.details.recentTransactions')}</h3>
                            </div>
                            <div className="border rounded-md overflow-hidden overflow-x-auto">
                              {transactions.length > 0 ? (
                                <table className="w-full text-xs min-w-[500px]">
                                  <thead className="bg-gray-50 text-gray-500 border-b">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-medium">{t('solide:clients.transactions.date')}</th>
                                      <th className="px-3 py-2 text-left font-medium">{t('solide:clients.transactions.type')}</th>
                                      <th className="px-3 py-2 text-right font-medium">{t('solide:clients.transactions.amount')}</th>
                                      <th className="px-3 py-2 text-right font-medium">{t('solide:clients.transactions.doneBy')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {transactions.map((t, k) => (
                                      <tr key={k} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-gray-600 flex items-center gap-2">
                                          <Calendar className="h-3 w-3 text-gray-400" />
                                          {new Date(t.date).toLocaleDateString()}
                                        </td>
                                        <td className="px-3 py-2 text-gray-900">{t.type?.replace('_', ' ')}</td>
                                        <td className={`px-3 py-2 text-right font-medium ${financier.devise === 'USD' ? 'text-emerald-600' : 'text-gray-900'}`}>
                                          {t.montant?.toLocaleString()} {financier.devise === 'USD' ? '$' : 'G'}
                                        </td>
                                        <td className="px-3 py-2 text-right text-gray-500">{t.fait_par || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="p-6 text-center text-gray-400 text-xs">{t('solide:clients.transactions.noTransactions')}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Assignation Agent */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-[400px] p-0 bg-white gap-0">
          <div className="p-3 border-b">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base text-blue-800">
                <UserPlus className="h-5 w-5" />
                {t('solide:clients.assignModal.title')}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          <form onSubmit={handleAssignAgent} className="p-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-gray-500 uppercase">{t('solide:clients.assignModal.clientConcerned')}</Label>
              <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-md flex items-center gap-2">
                 <div className="h-8 w-8 rounded-full bg-white border border-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                    {selectedClientForAssign?.client?.nom_complet?.charAt(0)}
                 </div>
                 <div>
                    <div className="text-sm font-bold text-blue-900">{selectedClientForAssign?.client?.nom_complet}</div>
                    <div className="text-[10px] text-blue-600 font-mono">{selectedClientForAssign?.compte?.code}</div>
                 </div>
              </div>
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="agent" className="text-[10px] font-medium text-gray-500 uppercase">{t('solide:clients.assignModal.agentUsername')}</Label>
              {loadingAgents ? (
                 <div className="h-8 flex items-center text-xs text-gray-400">{t('solide:clients.assignModal.loading')}</div>
              ) : (
                  <select
                    id="agent"
                    value={newAgentUsername}
                    onChange={(e) => setNewAgentUsername(e.target.value)}
                    className="w-full h-8 text-sm border rounded-md px-2 bg-white text-gray-700 focus:outline-none focus:border-blue-500"
                    required
                  >
                    <option value="">-- Sélectionner un agent --</option>
                    {agentsList.map(agent => (
                        <option key={agent.username} value={agent.username}>
                            {agent.first_name} {agent.last_name} ({agent.username})
                        </option>
                    ))}
                  </select>
              )}
              <p className="text-[10px] text-gray-400">{t('solide:clients.assignModal.selectAgentHint')}</p>
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="raison" className="text-[10px] font-medium text-gray-500 uppercase">{t('solide:clients.assignModal.reason')}</Label>
              <Input
                id="raison"
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder={t('solide:clients.assignModal.reasonPlaceholder')}
                className="h-8 text-sm"
                required
              />
            </div>
            
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setAssignModalOpen(false)} disabled={isAssigning} className="h-8 text-xs">
                {t('solide:clients.assignModal.cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={isAssigning || !newAgentUsername} className="bg-blue-600 hover:bg-blue-700 h-8 text-xs">
                {isAssigning ? (
                    <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div> {t('solide:clients.assignModal.assigning')}</>
                ) : t('solide:clients.assignModal.confirm')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsBS;
